<template>
  <div>
    <NoteSection
      title="運営からの特記事項"
      :content="user.operatorNotice || ''"
      :editable="true"
      :can-edit="canEditOperatorNote"
      :is-editing="operatorNote.isEditing"
      :draft="operatorNote.draft"
      :is-saving="operatorNote.isSaving"
      @toggle-edit="toggleEdit(operatorNote, 'operatorNotice', canEditOperatorNote)"
      @update:draft="operatorNote.draft = $event"
      @cancel-edit="cancelEdit(operatorNote)"
    />

    <NoteSection
      title="ユーザーの特記事項"
      :content="user.notice || ''"
      :editable="userNoteEditable"
      :can-edit="canEditUserNote"
      :is-editing="userNote.isEditing"
      :draft="userNote.draft"
      :is-saving="userNote.isSaving"
      @toggle-edit="toggleEdit(userNote, 'notice', canEditUserNote)"
      @update:draft="userNote.draft = $event"
      @cancel-edit="cancelEdit(userNote)"
    />
  </div>
</template>

<script setup>
// TODO: 10/7リリースの切り戻し用（#2599 フラグA）。新運用が安定したら削除する
// 特記事項統合（#2517）前の「運営からの特記事項」「ユーザーの特記事項」の2欄を users.operator_notice / users.notice で読み書きする。
import { computed, reactive, watch } from "vue";
import { useStore } from "vuex";
import NoteSection from "./NoteSection.vue";
import userApi from "@/vue/api/users";

const props = defineProps({
  user: { type: Object, required: true },
  // ユーザーの特記事項だけに適用する（いつでも相談の回答編集可否など）。運営からの特記事項はロールだけで判定する。
  userNoteEditable: { type: Boolean, default: false },
  canEditUserNote: { type: Boolean, default: false },
});

const emit = defineEmits(["saved"]);

const store = useStore();

// 運営からの特記事項は運営権限のみ編集でき、他の相談員は閲覧だけにする（APIでも同条件で403）。
const canEditOperatorNote = computed(() => {
  const hasAllCapabilities = store.getters.hasAllCapabilities;
  if (typeof hasAllCapabilities !== "function") return false;
  return hasAllCapabilities(["operator:access"]);
});

const operatorNote = reactive({ isEditing: false, draft: "", isSaving: false });
const userNote = reactive({ isEditing: false, draft: "", isSaving: false });

const resetNote = (note) => {
  note.isEditing = false;
  note.draft = "";
  note.isSaving = false;
};

// サイドバーは相談を切り替えても本コンポーネントを使い回すため、前ユーザーの下書きを別ユーザーへ保存させない
watch(() => props.user.id, () => {
  resetNote(operatorNote);
  resetNote(userNote);
});

// 相談画面の下書き保存等は Vuex の user.notice を送るため、ここでの保存内容を反映しておかないと古い値で上書きされる。
const syncVuexUser = (userId, patch) => {
  const vuexUser = store.getters.user;
  if (vuexUser && vuexUser.id != null && vuexUser.id === userId) {
    Object.assign(vuexUser, patch);
  }
};

const updateUser = (params) => new Promise((resolve, reject) => {
  userApi.updateUser(params, resolve, reject);
});

const toggleEdit = async (note, field, canEdit) => {
  if (!canEdit) return;

  if (!note.isEditing) {
    note.draft = props.user[field] || "";
    note.isEditing = true;
    return;
  }

  const userId = props.user.id;
  const draft = note.draft;
  // 保存中に別ユーザーへ切り替わった場合、完了後の状態更新を切替後のユーザーに反映しない
  const isStale = () => props.user.id !== userId;

  note.isSaving = true;
  try {
    await updateUser({ id: userId, [field]: draft });
  } finally {
    if (!isStale()) note.isSaving = false;
  }

  syncVuexUser(userId, { [field]: draft });
  if (isStale()) return;

  note.isEditing = false;
  emit("saved");
};

const cancelEdit = (note) => {
  note.isEditing = false;
  note.draft = "";
};
</script>
